import { CheckColour, CheckState, OptionType } from '../../components/Options/Options';
import { BRAND_COLOURS } from './variants';
import { IColoursCollection } from './colourInterfaces';
import { interactivityColour } from './eoraColourUtils';
import { PX_TO_REM } from '../sizes/sizes';

export interface ColourConfig {
  uncheckColour?: string;
  checkColour?: string;
}

export const COLOUR_CONFIGS = {
  [CheckColour.DEFAULT]: {
    getUncheckColour: (collections: IColoursCollection) => collections.eoraCoreColours.BORDER_COLOR.COLOR_BORDER_DEFAULT,
    getCheckColour: (collections: IColoursCollection) => collections.primary.colour
  },
  [CheckColour.SUCCESS]: {
    getUncheckColour: (collections: IColoursCollection) => collections.status.success.colour,
    getCheckColour: (collections: IColoursCollection) => collections.status.success.colour
  },
  [CheckColour.INFO]: {
    getUncheckColour: (collections: IColoursCollection) => collections.status.info.colour,
    getCheckColour: (collections: IColoursCollection) => collections.status.info.colour
  },
  [CheckColour.WARNING]: {
    getUncheckColour: (collections: IColoursCollection) => collections.status.warning.colour,
    getCheckColour: (collections: IColoursCollection) => collections.status.warning.colour
  },
  [CheckColour.ERROR]: {
    getUncheckColour: (collections: IColoursCollection) => collections.status.error.colour,
    getCheckColour: (collections: IColoursCollection) => collections.status.error.colour
  }
} as const;

const getColourConfig = (colour: CheckColour, coloursCollection: IColoursCollection, bgOverride?: string): ColourConfig => {
  const config = COLOUR_CONFIGS[colour];
  const uncheckColour = config.getUncheckColour(coloursCollection);
  const checkColour = config.getCheckColour(coloursCollection);

  return {
    checkColour: bgOverride ?? checkColour,
    uncheckColour: uncheckColour
  };
};

export const generateProgressBarColours = ({ primary, eoraCoreColours }: IColoursCollection, bgOverride?: string) => ({
  [BRAND_COLOURS.ACCENT]: {
    text: bgOverride ?? primary.colour,
    background: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_15
  }
});

export const generateOptionColours = (coloursCollection: IColoursCollection, bgOverride?: string, colour: CheckColour = CheckColour.DEFAULT) => {
  const { palette, text, eoraCoreColours } = coloursCollection;
  const colourConfig = getColourConfig(colour, coloursCollection, bgOverride);

  return {
    [CheckState.UNCHECKED]: {
      text: eoraCoreColours.BASE.WHITE_NOSWITCH,
      background: eoraCoreColours.BACKGROUND_COLOR.INPUT_COLOR_BACKGROUND,
      border: `${PX_TO_REM[1]} solid ${colourConfig.uncheckColour}`,
      [OptionType.SWITCH]: {
        background: eoraCoreColours.BACKGROUND_COLOR.SWITCH_COLOR_BACKGROUND,
        border: `0px`
      }
    },
    [CheckState.CHECKED]: {
      text: eoraCoreColours.BASE.WHITE_NOSWITCH,
      background: colourConfig.checkColour,
      border: colourConfig.checkColour,
      [OptionType.SWITCH]: {
        background: colourConfig.checkColour,
        border: `0px`
      }
    },
    [CheckState.DISABLED]: {
      text: text.disabled,
      background: interactivityColour(eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_3, eoraCoreColours.BACKGROUND_COLOR.INPUT_COLOR_BACKGROUND),
      border: `${PX_TO_REM[1]} solid ${eoraCoreColours.BORDER_COLOR.COLOR_BORDER_DISABLED}`,
      [OptionType.SWITCH]: {
        background: interactivityColour(eoraCoreColours.OPACITIES.COLOR_LIGHT_OPACITY_60, eoraCoreColours.BACKGROUND_COLOR.SWITCH_COLOR_BACKGROUND),
        border: `0px`
      },
      label: {
        text: text.disabled
      }
    },
    wrapper: {
      default: {
        background: palette.neutral15
      },
      defaultSelected: {
        background: interactivityColour(eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_PRESSED_DARK_SOFT, palette.neutral15)
      },
      hover: {
        background: interactivityColour(eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_HOVER_DARK_SOFT, palette.neutral15)
      },
      active: {
        background: interactivityColour(eoraCoreColours.BACKGROUND_COLOR.INTERACTIVE_STATES_BG.COLOR_BACKGROUND_TINT_PRESSED_DARK_SOFT, palette.neutral15)
      },
      disabled: {
        background: eoraCoreColours.BACKGROUND_COLOR.INPUT_COLOR_BACKGROUND_DISABLED
      }
    }
  };
};

// Shared between Options (Checkbox, Switch, Radio) and Progressbar components
// useful when overriding ui controls in theming (cmc, boq)
export const generateUIControlsColours = (coloursCollection: IColoursCollection, bgOverride?: string, colour: CheckColour = CheckColour.DEFAULT) => ({
  optionColours: generateOptionColours(coloursCollection, bgOverride, colour),
  progressBarColours: generateProgressBarColours(coloursCollection, bgOverride)
});
